import { supabase } from "@/integrations/supabase/client";
import type { EdgeFunctionResponse } from "@/types/edge-functions";

/**
 * Retry strategy for handling transient failures:
 * - linear: Fast delays for user-facing ops (200ms, 400ms, 600ms)
 * - immediate: Minimal delay for reads (0ms, 200ms, 500ms)
 * - exponential: For rate limits/background jobs (1s, 2s, 4s)
 * - adaptive: Uses exponential for 429, linear otherwise
 */
export type RetryStrategy = "linear" | "immediate" | "exponential" | "adaptive";

export interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  strategy?: RetryStrategy;
  retryableStatusCodes?: number[];
  idempotent?: boolean;
  /** Skip session check — use for public edge functions called from unauthenticated contexts */
  skipAuth?: boolean;
}

const DEFAULT_RETRY_OPTIONS: Required<Omit<RetryOptions, "idempotent" | "skipAuth">> & {
  idempotent: boolean;
  skipAuth: boolean;
} = {
  maxRetries: 2,
  retryDelay: 200, // Base delay in ms
  strategy: "linear",
  retryableStatusCodes: [408, 429, 500, 502, 503, 504],
  idempotent: false,
  skipAuth: false,
};

/**
 * Calculates retry delay based on strategy and attempt number.
 * - linear: baseDelay * attempt (200ms, 400ms, 600ms)
 * - immediate: 0 for first retry, then baseDelay * min(attempt, 2)
 * - exponential: baseDelay * 2^attempt (for rate limits)
 * - adaptive: exponential for 429, linear otherwise
 */
function calculateDelay(
  attempt: number,
  strategy: RetryStrategy,
  baseDelay: number,
  lastError?: unknown
): number {
  if (strategy === "adaptive" && lastError) {
    const err = lastError as { status?: number };
    if (err.status === 429) {
      return baseDelay * Math.pow(2, attempt);
    }
    return baseDelay * attempt;
  }

  switch (strategy) {
    case "exponential":
      return baseDelay * Math.pow(2, attempt);
    case "linear":
      return baseDelay * attempt;
    case "immediate":
      return attempt === 0 ? 0 : baseDelay * Math.min(attempt, 2);
    case "adaptive":
      return baseDelay * attempt;
    default:
      return baseDelay * attempt;
  }
}

/**
 * Determines if an error is retryable based on status code and error type
 */
function isRetryableError(error: unknown, retryableStatusCodes: number[]): boolean {
  if (error && typeof error === "object" && "status" in error) {
    const status = error.status as number;
    return retryableStatusCodes.includes(status);
  }

  // Network errors, timeouts, etc.
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes("network") ||
      message.includes("timeout") ||
      message.includes("fetch") ||
      message.includes("connection")
    );
  }

  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Invokes a Supabase Edge Function with retry logic and idempotency handling
 *
 * @param functionName - Name of the Edge Function to invoke
 * @param body - Request body (optional)
 * @param options - Retry configuration options
 * @returns Promise resolving to the function response
 *
 * @example
 * ```typescript
 * const result = await invokeEdgeFunction<ListUsersResponse>(
 *   "list-users",
 *   undefined,
 *   { maxRetries: 3, idempotent: true }
 * );
 * ```
 */
export async function invokeEdgeFunction<T = unknown>(
  functionName: string,
  body?: unknown,
  options: RetryOptions = {}
): Promise<T> {
  const config = { ...DEFAULT_RETRY_OPTIONS, ...options };
  const { maxRetries, retryDelay, strategy, retryableStatusCodes, idempotent, skipAuth } = config;

  let lastError: unknown;
  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      let authHeaders: Record<string, string> = {};

      if (!skipAuth) {
        // Get session for auth token
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          throw new Error("Not authenticated");
        }

        authHeaders = { Authorization: `Bearer ${session.access_token}` };
      }

      // Invoke the function
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: body as Record<string, unknown> | undefined,
        headers: authHeaders,
      });

      // Handle Supabase function errors
      if (error) {
        // Don't retry on client errors (4xx) unless explicitly configured
        const errorStatus = (error as { status?: number }).status;
        if (errorStatus && errorStatus >= 400 && errorStatus < 500) {
          // Check if it's a retryable 4xx error (like 408, 429)
          if (!retryableStatusCodes.includes(errorStatus)) {
            throw error;
          }
        }

        // For retryable errors, throw to trigger retry logic
        if (isRetryableError(error, retryableStatusCodes)) {
          throw error;
        }

        // Non-retryable errors should be thrown immediately
        throw error;
      }

      // Check response structure
      const response = data as EdgeFunctionResponse<T>;

      // Handle error responses from the function
      if (!response.success && response.error) {
        // For idempotent operations, check if error indicates already completed
        if (idempotent && response.error) {
          const errorLower = response.error.toLowerCase();
          // Common idempotency indicators
          if (
            errorLower.includes("already exists") ||
            errorLower.includes("already processed") ||
            errorLower.includes("already deleted") ||
            errorLower.includes("duplicate")
          ) {
            // Operation already completed - return success
            return response as T;
          }
        }

        // Non-idempotent errors or non-idempotency-indicating errors should be thrown
        throw new Error(response.error);
      }

      // Success - return the data
      return (response.data ?? response) as T;
    } catch (error) {
      lastError = error;

      // Don't retry if we've exhausted retries
      if (attempt >= maxRetries) {
        break;
      }

      // Don't retry non-retryable errors
      if (!isRetryableError(error, retryableStatusCodes)) {
        throw error;
      }

      const delay = calculateDelay(attempt, strategy, retryDelay, error);
      if (delay > 0) {
        await sleep(delay);
      }

      attempt++;
    }
  }

  // If we get here, all retries failed
  throw lastError || new Error(`Failed to invoke ${functionName} after ${maxRetries} retries`);
}

/**
 * Idempotency helper: Checks if an operation should be considered already completed
 * based on error messages
 */
export function isIdempotentError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const errorLower = error.message.toLowerCase();
  return (
    errorLower.includes("already exists") ||
    errorLower.includes("already processed") ||
    errorLower.includes("already deleted") ||
    errorLower.includes("duplicate") ||
    errorLower.includes("not found") // For delete operations, "not found" means already deleted
  );
}
